import React, { useState, useEffect, useContext, createContext } from "react";
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import loginApi from "./backend/loginApi";
import userApi from "./backend/userApi";
import { AuthError, ValidationError, HttpError } from "../helpers/custom-errors";
import { isArrayWithLength } from "../helpers/commons";
import { setLastSchoolId } from "../helpers/resolveSchoolId";
import {Roles} from "../config/Roles";

const authContext = createContext();

// Provider component that wraps your app and makes auth object ...
// ... available to any child component that calls useAuth().
export function ProvideAuth({ children }) {
  const auth = useProvideAuth();
  return <authContext.Provider value={auth}>{children}</authContext.Provider>;
}

// Hook for child components to get the auth object ...
// ... and re-render when it changes.
export const useAuth = () => {
  return useContext(authContext);
};

// Provider hook that creates auth object and handles state
function useProvideAuth() {
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const [globalError, setGlobalError] = useState(null);
  
  const { t } = useTranslation();
  
  
  // Whenever the backend hands back a session with a currentSchool selected, mirror it into
  // localStorage. Both flows land here — the manual selectSchool call and the auto-selection
  // that fires when a single-school user logs in — so shortcut/legacy routes (SchoolShortcut)
  // resolve to the last thing the user actually saw, not just the deterministic first-of-scope
  // fallback. Cheap on every setUser; noop when currentSchool is absent.
  const rememberCurrentSchool = (userData) => {
	  const currentId = userData && userData.currentSchool && userData.currentSchool.id;
	  if (currentId) setLastSchoolId(currentId);
  };

  const loginUser = ({ values }) =>
	new Promise((resolve, reject) => {
		loginApi.loginUser(values).then(response => {
			setStatus(response.status);
			setUser(response.userData);
			rememberCurrentSchool(response.userData);
			resolve(response);
		}).catch(error => reject(error));
  });

  const changeUserPassword = ({ values }) =>
	new Promise((resolve, reject) => {
		loginApi.changeUserPassword(values).then(response => {
			setStatus(response.status);
			setUser(response.userData);
			rememberCurrentSchool(response.userData);
			resolve(response);
		}).catch(error => reject(error));
  });

  const selectSchool = ({ values }) =>
	new Promise((resolve, reject) => {
		loginApi.selectSchool(values).then(response => {
			setStatus(response.status);
			setUser(response.userData);
			rememberCurrentSchool(response.userData);
			resolve(response);
		}).catch(error => reject(error));
  });

  // Valida invitationToken sem consumir (landing /invite/:token). NÃO seta
  // globalError em invalid token — a página tem estado dedicado de "link
  // expirado" e propagar erro global vaza pra próxima rota (ex.: forgot-password).
  const verifyInvitationToken = ({ values }) =>
	new Promise((resolve, reject) => {
		loginApi.verifyInvitationToken(values).then(response => {
			resolve(response);
		}).catch(error => reject(error));
  });

  // Accept invite (define senha do convite + auto-login). Mesmo shape do
  // loginUser — backend retorna userData/status COMPLETED, popula authStore,
  // caller pode navigate('/') sem passar pela tela de login.
  //
  // <p>Convite pode ser aberto em browser que já tinha OUTRO user logado em
  // outra tab (cookies + localStorage compartilhados). Sem reset, o invitee
  // herda o `reeduca:lastSchoolId` do user anterior — se aquele school não
  // estiver no scope do invitee, home resolve pra URL inválida → gate rejeita
  // → login. Limpamos state + localStorage ANTES pra deixar a resposta do
  // backend ser a fonte fresh.
  const acceptInvite = ({ values }) =>
	new Promise((resolve, reject) => {
		try { window.localStorage.removeItem('reeduca:lastSchoolId'); } catch (e) { /* private mode etc */ }
		setUser(null);
		setStatus(null);

		loginApi.acceptInvite(values).then(response => {
			setStatus(response.status);
			setUser(response.userData);
			rememberCurrentSchool(response.userData);
			resolve(response);
		}).catch(error => reject(error));
  });
  
  const getCurrentSchoolId = () => {
	return (user && user.currentSchool && user.currentSchool.id)
		? user.currentSchool.id
		: 0
	};

 /* const selectUserWorkgroup = ({ values }) =>
	new Promise((resolve, reject) => {
		loginApi.selectUserWorkgroup(values).then(response => {
			setStatus(response.status);
			setUser(response.userData);
			resolve(response);
		}).catch(error => reject(error));
  });*/
	
  // Refactor 2026-07-28 (memory project_toast_debt_state_vs_event): não seta
  // mais globalSuccess (estado que sobrevive a re-renders emitia toast
  // duplicado). Handler no TopbarActions chama onSuccess() no .then() —
  // toast vira EVENTO, não estado. `response.success` fica exposto no
  // resolve pra caller montar a mensagem.
  const logoutUser = () =>
	new Promise((resolve, reject) => {
		loginApi.logoutUser().then(response => {
			setUser(null);
			setStatus(null);
			resolve(response);
		}).catch(error => reject(error));
  });
	
 const onUnathorized = (error) => {
	// 401 = sem sessão → kickar pra login. 403 = sessão OK, sem permissão pra
	// ESSE recurso → NÃO deslogar (senão fluxo de invite com school out-of-scope
	// vira logout do invitee que acabou de logar). setGlobalError sobe o toast
	// pra tela; caller decide se navega away do recurso protegido.
	if (error && error.status === 403) {
		setGlobalError(error);
		return;
	}
	setUser(null);
	setStatus(null);
	setGlobalError(error);
  };
  
  const isAdmin = () => {
	  return (user 
			  && isArrayWithLength(user.roles) 
			  && (user.roles.indexOf(Roles.ADMIN) !== -1 || user.roles.indexOf(Roles.ROOT) !== -1 ))
  };

  const isRoot = () => {
	  return (user 
			  && isArrayWithLength(user.roles) 
			  && (user.roles.indexOf(Roles.ROOT) !== -1 ))
  };
  
  const hasPermission = (permission) => {
	  return (user 
			  && isArrayWithLength(user.permissions) 
			  && (user.permissions.indexOf(permission) !== -1))
  };
	
  const sendUserPasswordResetEmail = ({ values }) =>
	new Promise((resolve, reject) => {
		loginApi.sendUserPasswordResetEmail(values).then(response => {
			resolve(response);
		}).catch(error => reject(error));
  });
	
  const confirmUserPasswordReset = ({ values }) =>
	new Promise((resolve, reject) => {
		loginApi.confirmUserPasswordReset(values).then(response => {
			resolve(response);
		}).catch(error => reject(error));
  });
	
  const verifyUserPasswordResetToken = ({ values }) =>
	new Promise((resolve, reject) => {
		loginApi.verifyUserPasswordResetToken(values).then(response => {
			resolve(response);
		}).catch(error => reject(error));
  });

  // Sincroniza identidade do user com Sentry — 1 effect cobre os 6 setters
  // (loginUser, changeUserPassword, selectSchool, updateUserProfile, restore
  // no useEffect abaixo, logoutUser/onUnathorized que zeram). Erros passam a
  // vir tageados com email + schoolId — dá pra filtrar dashboard por escola.
  useEffect(() => {
    if (!user) {
      Sentry.setUser(null);
      return;
    }
    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: user.firstName,
      schoolId: user.currentSchool && user.currentSchool.id,
    });
  }, [user]);

  useEffect(() => {

	  let isMounted = true;

	  loginApi.getUserAuthState()
		.then((response) => {
			if (isMounted) {
				setUser(response.userData);
				setStatus(response.status);
				rememberCurrentSchool(response.userData);
			}
		})
		.catch(error => {		
			if (isMounted) {
				if (error instanceof AuthError)	{	
					if (error.code === "auth-02") {
						setGlobalError(error);
					}
				} else {
					setGlobalError(new HttpError('GENERIC', 500, t('error.api.general')));
				}
			}
		})
	   .finally(() => {
		   if (isMounted) setInitialized(true);
		});

	    return () => { isMounted = false }; 
	    
  }, []);
  
  /** Refaz GET /auth pra atualizar o `user` em memória (schools, roles, etc).
   *  Usado após mutations que mexem no scope do próprio user — ex.: editar
   *  marca da escola no form: o modal Trocar Escola precisa refletir bg/icon
   *  novo sem esperar F5. Silencioso em erro (não interrompe UX pós-save). */
  const refreshUser = () => loginApi.getUserAuthState()
	.then((response) => {
		setUser(response.userData);
		setStatus(response.status);
		rememberCurrentSchool(response.userData);
		return response;
	})
	.catch(() => { /* silencioso — mutation já sucedeu, refresh é bônus */ });

  // Return the user object and auth methods
  return {
	onUnathorized,
	globalError,
	initialized,
	status,
	user,
	isAdmin,
	isRoot,
    loginUser,
    logoutUser,
    changeUserPassword,
    selectSchool,
    refreshUser,
    sendUserPasswordResetEmail,
    confirmUserPasswordReset,
    verifyUserPasswordResetToken,
    verifyInvitationToken,
    acceptInvite,
    hasPermission,
    getCurrentSchoolId
  };
}